import type { components } from '@/types/apiSchema';

type AvailabilityPreferencesRequest =
  components['schemas']['AvailabilityPreferencesRequest'];
type AvailabilityPreferencesResponse =
  components['schemas']['AvailabilityPreferencesResponse'];

export const weekdays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type Weekday = (typeof weekdays)[number];

/** One recurring window, as `HH:mm` in the hiring manager's timezone. */
export interface AvailabilityWindow {
  startsAt: string;
  endsAt: string;
}

export interface AvailabilityDay {
  enabled: boolean;
  windows: AvailabilityWindow[];
}

export type WeeklyAvailability = Record<Weekday, AvailabilityDay>;

export interface InterviewPreferences {
  defaultMeetingDurationMinutes: number;
  timezone: string;
  weeklyAvailability: WeeklyAvailability;
}

const defaultWindow: AvailabilityWindow = {
  startsAt: '09:00',
  endsAt: '17:00',
};

/** The half-hour grid the editor offers runs to 23:30. */
const lastSelectableMinute = 23 * 60 + 30;
const addedWindowMinutes = 4 * 60;

/**
 * Mon–Fri 09:00–17:00. Shown as the starting point of the editor on a first
 * visit; it is only persisted once the hiring manager saves it.
 */
export const createDefaultWeeklyAvailability = (): WeeklyAvailability => ({
  monday: { enabled: true, windows: [{ ...defaultWindow }] },
  tuesday: { enabled: true, windows: [{ ...defaultWindow }] },
  wednesday: { enabled: true, windows: [{ ...defaultWindow }] },
  thursday: { enabled: true, windows: [{ ...defaultWindow }] },
  friday: { enabled: true, windows: [{ ...defaultWindow }] },
  saturday: { enabled: false, windows: [] },
  sunday: { enabled: false, windows: [] },
});

const createDefaultInterviewPreferences = (): InterviewPreferences => ({
  defaultMeetingDurationMinutes: 60,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  weeklyAvailability: createDefaultWeeklyAvailability(),
});

/** `09:00`, whether the API sends `09:00` or `09:00:00`. */
const toHourMinute = (time: string) => time.slice(0, 5);

const toMinutes = (time: string) => {
  const [hours, minutes] = toHourMinute(time).split(':');
  return Number(hours) * 60 + Number(minutes);
};

const toTime = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(
    minutes % 60,
  ).padStart(2, '0')}`;

/**
 * The window "Add window" should offer: four hours starting where the day's
 * last window ends, so a second window never lands on top of the first one.
 * `null` when the day already runs to the end of the grid and there is no
 * room left.
 */
export const nextWindowAfter = (
  windows: AvailabilityWindow[],
): AvailabilityWindow | null => {
  if (windows.length === 0) return { ...defaultWindow };

  const lastEnd = Math.max(
    ...windows.map((window) => toMinutes(window.endsAt)),
  );
  // A window saved off the half-hour grid still starts the next one on it.
  const startsAt = Math.ceil(lastEnd / 30) * 30;
  if (startsAt >= lastSelectableMinute) return null;

  return {
    startsAt: toTime(startsAt),
    endsAt: toTime(
      Math.min(startsAt + addedWindowMinutes, lastSelectableMinute),
    ),
  };
};

export const fromAvailabilityPreferencesResponse = (
  preferences: AvailabilityPreferencesResponse | null,
): InterviewPreferences => {
  if (!preferences) {
    return createDefaultInterviewPreferences();
  }

  return {
    defaultMeetingDurationMinutes: preferences.defaultMeetingDurationMinutes,
    timezone: preferences.timezone,
    weeklyAvailability: fromWeeklyAvailabilityResponse(
      preferences.weeklyAvailability,
    ),
  };
};

// A day the API omits is a day the hiring manager is not available on, so it
// never falls back to the default window.
const fromWeeklyAvailabilityResponse = (
  weeklyAvailability: AvailabilityPreferencesResponse['weeklyAvailability'],
): WeeklyAvailability =>
  Object.fromEntries(
    weekdays.map((weekday) => {
      const day = weeklyAvailability[weekday];
      const windows = (day?.intervals ?? []).map((interval) => ({
        startsAt: toHourMinute(interval.startsAt),
        endsAt: toHourMinute(interval.endsAt),
      }));

      return [weekday, { enabled: Boolean(day?.enabled), windows }];
    }),
  ) as WeeklyAvailability;

export const toAvailabilityPreferencesRequest = (
  preferences: InterviewPreferences,
): AvailabilityPreferencesRequest => ({
  defaultMeetingDurationMinutes: preferences.defaultMeetingDurationMinutes,
  timezone: preferences.timezone,
  weeklyAvailability: Object.fromEntries(
    weekdays.map((weekday) => {
      const day = preferences.weeklyAvailability[weekday];
      return [
        weekday,
        {
          enabled: day.enabled,
          intervals: day.enabled ? day.windows : [],
        },
      ];
    }),
  ),
});

/**
 * Why a day's windows cannot be saved, or `null` when they can. Overlapping
 * windows would let Finlay offer the same slot twice.
 */
export const dayWindowsError = (day: AvailabilityDay): string | null => {
  if (!day.enabled) return null;
  if (day.windows.length === 0) return 'Add a window or turn the day off.';

  const ends = day.windows.some(
    (window) => toMinutes(window.endsAt) <= toMinutes(window.startsAt),
  );
  if (ends) return 'Each window must end after it starts.';

  const sorted = [...day.windows].sort(
    (a, b) => toMinutes(a.startsAt) - toMinutes(b.startsAt),
  );
  const overlaps = sorted.some((window, index) => {
    const previous = sorted[index - 1];
    return previous
      ? toMinutes(window.startsAt) < toMinutes(previous.endsAt)
      : false;
  });

  return overlaps ? 'Windows on the same day cannot overlap.' : null;
};

/** The days that cannot be saved, keyed by weekday. Empty when all are valid. */
export const weeklyAvailabilityErrors = (
  weeklyAvailability: WeeklyAvailability,
): Partial<Record<Weekday, string>> => {
  const errors: Partial<Record<Weekday, string>> = {};

  for (const weekday of weekdays) {
    const error = dayWindowsError(weeklyAvailability[weekday]);
    if (error) errors[weekday] = error;
  }

  return errors;
};
